import { limitedCartRequest } from '@theme/product-purchase-limit';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * "Buy More & Save More" bundle packs.
 *
 * Renders inside the product form (see `blocks/bundle-packs.liquid`) and takes over the form's
 * submit so a pack of 1/2/3 items is added as a single cart operation. `blocks/buy-buttons.liquid`
 * points `product-form-component`'s `on:submit` at this component when
 * `product.metafields.custom.bundle_pack` is true, so the theme's single-variant add never runs.
 *
 * @typedef {object} BundleVariant
 * @property {string} id
 * @property {string} title
 * @property {number} price - In the currency's minor units (cents).
 * @property {number} compare - Compare-at price in minor units, 0 when unset.
 * @property {boolean} available
 * @property {number} mediaId - The variant's featured media, or 0 when none is assigned.
 *
 * @typedef {object} BundlePack
 * @property {HTMLElement} el
 * @property {number} size - Units in the pack (1, 2 or 3).
 * @property {string} title
 * @property {'none' | 'percentage' | 'fixed_total'} discountType
 * @property {number} discountValue - Percentage, or the fixed pack total in minor units.
 * @property {string} discountCode
 * @property {string} savingsTemplate
 * @property {HTMLInputElement | null} radio
 * @property {HTMLSelectElement[]} selects
 * @property {HTMLElement | null} priceEl
 * @property {HTMLElement | null} compareEl
 * @property {HTMLElement | null} savingsEl
 * @property {HTMLElement | null} flavorsWrap
 * @property {HTMLElement | null} flavorsHeader
 *
 * @typedef {object} BundlePacksRefs
 * @property {HTMLElement} [error]
 * @property {HTMLScriptElement} [variantData]
 *
 * @extends {Component<BundlePacksRefs>}
 */
class BundlePacksComponent extends Component {
  /** @type {Map<string, BundleVariant>} */
  #variants = new Map();

  /** @type {BundlePack[]} */
  #packs = [];

  /** @type {BundleVariant | undefined} */
  #fallbackVariant;

  #busy = false;

  /** @type {string | undefined} */
  #displayedMediaId;
  /** @type {string | undefined} */
  #requestedMediaId;
  /** @type {AbortController | undefined} */
  #mediaController;

  connectedCallback() {
    super.connectedCallback();

    this.#displayedMediaId = String(this.dataset.initialMediaId || '0');

    this.#readVariants();
    this.#readPacks();
    this.#initializeExpansionState();
    this.#update();
    this.#bindFlavorHeaderEvents();
  }

  /**
   * The Section Rendering API can morph this subtree (combined listings, quick add), which replaces
   * the cached pack elements.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#mediaController?.abort();
    this.#requestedMediaId = undefined;
    this.#displayedMediaId = String(this.dataset.initialMediaId || '0');

    this.#readVariants();
    this.#readPacks();
    this.#initializeExpansionState();
    this.#update();
    this.#bindFlavorHeaderEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaController?.abort();
  }

  /* -------------------------------------------------------------------------- */
  /* Setup                                                                       */
  /* -------------------------------------------------------------------------- */

  #readVariants() {
    const raw = this.refs.variantData?.textContent;
    if (!raw) return;

    this.#variants.clear();

    try {
      /** @type {BundleVariant[]} */
      const variants = JSON.parse(raw);
      for (const variant of variants) {
        this.#variants.set(String(variant.id), variant);
      }
      this.#fallbackVariant = variants.find((variant) => variant.available) ?? variants[0];
    } catch (error) {
      console.warn('[bundle-packs] Could not parse variant data', error);
    }
  }

  #readPacks() {
    this.#packs = Array.from(this.querySelectorAll('.bundle-pack')).map((element) => {
      const el = /** @type {HTMLElement} */ (element);

      return {
        el,
        size: Number(el.dataset.packSize) || 1,
        title: el.dataset.packTitle ?? '',
        discountType: /** @type {BundlePack['discountType']} */ (el.dataset.discountType || 'none'),
        discountValue: Number(el.dataset.discountValue) || 0,
        discountCode: el.dataset.discountType === 'none' ? '' : (el.dataset.discountCode ?? '').trim(),
        savingsTemplate: el.dataset.savingsTemplate ?? '',
        radio: el.querySelector('input[type="radio"]'),
        selects: /** @type {HTMLSelectElement[]} */ (Array.from(el.querySelectorAll('select'))),
        priceEl: el.querySelector('[data-bundle-price]'),
        compareEl: el.querySelector('[data-bundle-compare]'),
        savingsEl: el.querySelector('[data-bundle-savings]'),
        flavorsWrap: el.querySelector('.bundle-pack__flavors-wrap'),
        flavorsHeader: el.querySelector('.bundle-pack__flavors-header'),
      };
    });
  }

  /**
   * Initialize expansion state:
   * - Pre-selected packs start with data-expanded="false" (collapsed)
   * - Only when user clicks/interacts does it expand
   */
  #initializeExpansionState() {
    for (const pack of this.#packs) {
      const isSelected = pack.el.dataset.selected === 'true';
      if (isSelected) {
        // Pre-selected, but collapsed initially
        pack.el.dataset.expanded = 'false';
      } else {
        pack.el.dataset.expanded = 'false';
      }
    }
  }

  /**
   * Bind click events to the flavor header to toggle expansion
   */
  #bindFlavorHeaderEvents() {
    for (const pack of this.#packs) {
      const header = pack.flavorsHeader;
      if (header) {
        header.addEventListener('click', (event) => {
          event.stopPropagation();
          this.#toggleExpansion(pack);
        });
      }
    }
  }

  /**
   * Toggle expansion for a pack
   * @param {BundlePack} pack
   */
  #toggleExpansion(pack) {
    const isSelected = pack.el.dataset.selected === 'true';
    if (!isSelected) {
      // If not selected, select it first
      if (pack.radio) {
        pack.radio.checked = true;
        this.#update();
        // After selection, expand
        setTimeout(() => {
          pack.el.dataset.expanded = 'true';
        }, 50);
      }
      return;
    }

    // Toggle expansion
    const currentExpanded = pack.el.dataset.expanded === 'true';
    pack.el.dataset.expanded = currentExpanded ? 'false' : 'true';
  }

  /* -------------------------------------------------------------------------- */
  /* Event handlers (bound declaratively via `on:` attributes)                   */
  /* -------------------------------------------------------------------------- */

  /**
   * Handles both pack radios and flavor dropdowns — `change` bubbles to the root element.
   */
  handleChange() {
    this.#update();
  }

  /**
   * Selects a pack when the shopper clicks anywhere on its card.
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const origin = this.#origin(event);
    if (!origin) return;

    // Let the dropdowns, and the label/radio's own behaviour, work untouched.
    if (origin.closest('select, label, input')) return;

    // Also let the flavor header click be handled separately
    if (origin.closest('.bundle-pack__flavors-header')) return;

    const pack = this.#packs.find(({ el }) => el.contains(origin));
    if (!pack?.radio || pack.radio.checked) return;

    pack.radio.checked = true;
    this.#update();

    // When a pack is selected via click, expand it
    if (pack) {
      pack.el.dataset.expanded = 'true';
    }
  }

  /**
   * The element the event actually landed on. The theme's delegated listener proxies `event.target`
   * to the element carrying the `on:` attribute, so `composedPath` is the reliable source here.
   * @param {Event} event
   * @returns {Element | null}
   */
  #origin(event) {
    const first = event.composedPath?.()[0];

    if (first instanceof Element) return first;

    return event.target instanceof Element ? event.target : null;
  }

  /* -------------------------------------------------------------------------- */
  /* Rendering                                                                   */
  /* -------------------------------------------------------------------------- */

  #update() {
    for (const pack of this.#packs) {
      const selected = Boolean(pack.radio?.checked);
      pack.el.dataset.selected = String(selected);

      const flavors = pack.el.querySelector('.bundle-pack__flavors');
      flavors?.setAttribute('aria-hidden', String(!selected));

      this.#renderPrices(pack);
    }

    this.#validate();
    this.#updateMedia();
  }

  /** Keep the gallery tied to selection #1, without changing any bundle/cart selections. */
  async #updateMedia() {
    if (this.#quantityOnly) return;
    const firstSelect = this.#selectedPack?.selects[0];
    const variant = firstSelect ? this.#variants.get(firstSelect.value) : undefined;
    const mediaId = String(variant?.mediaId || '0');

    if (mediaId === this.#requestedMediaId) return;
    this.#mediaController?.abort();
    this.#requestedMediaId = undefined;

    // A variant without its own image leaves the current gallery intact.
    if (!variant || mediaId === '0' || mediaId === this.#displayedMediaId) return;

    const scope = this.closest('dialog, .shopify-section');
    const gallery = scope?.querySelector('media-gallery');
    if (!gallery || !this.dataset.productUrl || !this.dataset.sectionId) return;

    const controller = new AbortController();
    this.#mediaController = controller;
    this.#requestedMediaId = mediaId;

    const url = new URL(this.dataset.productUrl, window.location.origin);
    const view = new URL(window.location.href).searchParams.get('view');
    if (view) url.searchParams.set('view', view);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      // Rendering the selected variant also handles galleries with "hide other variants" enabled.
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Failed to render bundle media: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newGallery = html.querySelector('media-gallery');
      if (!newGallery || controller.signal.aborted || !this.isConnected) return;

      gallery.replaceWith(newGallery);
      this.#displayedMediaId = mediaId;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[bundle-packs] Could not update media', error);
    } finally {
      if (this.#mediaController === controller) this.#requestedMediaId = undefined;
    }
  }

  /** @param {BundlePack} pack */
  #renderPrices(pack) {
    const { compareTotal, total } = this.#totals(pack);
    const savings = Math.max(compareTotal - total, 0);

    if (pack.priceEl) pack.priceEl.textContent = this.#money(total);

    if (pack.compareEl) {
      pack.compareEl.textContent = savings > 0 ? this.#money(compareTotal) : '';
      pack.compareEl.hidden = savings <= 0;
    }

    if (pack.savingsEl) {
      const show = savings > 0 && pack.savingsTemplate !== '';
      pack.savingsEl.textContent = show ? this.#savingsText(pack, savings, compareTotal) : '';
      pack.savingsEl.hidden = !show;
    }
  }

  /**
   * `baseTotal` is what the units cost today, so the pack discount applies to that. `compareTotal`
   * also honours each variant's compare-at price, so a product already on sale shows the full saving.
   * @param {BundlePack} pack
   * @returns {{compareTotal: number, total: number}}
   */
  #totals(pack) {
    let baseTotal = 0;
    let compareTotal = 0;
    let counted = 0;

    /** @param {BundleVariant} variant */
    const add = (variant) => {
      baseTotal += variant.price;
      compareTotal += Math.max(variant.compare || 0, variant.price);
    };

    for (const select of pack.selects) {
      const variant = this.#variants.get(select.value);
      if (!variant) continue;

      add(variant);
      counted++;
    }

    // Keep prices visible before every flavor has been picked by pricing the remaining
    // units at the default variant's price.
    if (counted < pack.size && this.#fallbackVariant) {
      for (let index = counted; index < pack.size; index++) add(this.#fallbackVariant);
    }

    let total = baseTotal;

    if (pack.discountType === 'percentage') {
      const percentage = Math.min(Math.max(pack.discountValue, 0), 100);
      total = Math.round((baseTotal * (100 - percentage)) / 100);
    } else if (pack.discountType === 'fixed_total') {
      total = Math.max(pack.discountValue, 0);
    }

    return { compareTotal, total: Math.min(total, compareTotal) };
  }

  /**
   * @param {BundlePack} pack
   * @param {number} savings
   * @param {number} compareTotal
   */
  #savingsText(pack, savings, compareTotal) {
    const percent = compareTotal > 0 ? Math.round((savings / compareTotal) * 100) : 0;

    return pack.savingsTemplate.replace(/\[amount\]/g, this.#money(savings)).replace(/\[percent\]/g, `${percent}%`);
  }

  /** @param {number} amount - Minor units. */
  #money(amount) {
    return formatMoney(amount, this.dataset.moneyFormat || '{{ amount }}', this.dataset.currency || 'USD');
  }

  /* -------------------------------------------------------------------------- */
  /* Validation                                                                  */
  /* -------------------------------------------------------------------------- */

  get #selectedPack() {
    return this.#packs.find((pack) => pack.radio?.checked);
  }

  get #quantityOnly() {
    return this.dataset.quantityOnly === 'true';
  }

  get #quantityVariant() {
    return this.#variants.get(this.dataset.defaultVariantId || '') ?? this.#fallbackVariant;
  }

  /**
   * Every dropdown must resolve to an in-stock variant, or the quantity-only default must be available.
   * @param {BundlePack} [pack]
   */
  #isComplete(pack = this.#selectedPack) {
    if (!pack) return false;
    if (this.#quantityOnly) return this.#quantityVariant?.available === true;
    if (pack.selects.length !== pack.size) return false;

    return pack.selects.every((select) => this.#variants.get(select.value)?.available === true);
  }

  /**
   * While bundles are active the pack selection is the only thing that gates add-to-cart, so this
   * owns the button's disabled state and label outright.
   */
  #validate() {
    const complete = this.#isComplete();
    const anyAvailable = Array.from(this.#variants.values()).some((variant) => variant.available);
    const label = anyAvailable ? this.dataset.addToCartText : this.dataset.soldOutText;

    for (const container of this.#addToCartComponents) {
      const button = /** @type {HTMLButtonElement | null} */ (container.querySelector('[ref="addToCartButton"]'));
      if (!button) continue;

      button.disabled = !complete;
      if (label) this.#setButtonLabel(button, label);
    }

    if (complete) this.#hideError();

    return complete;
  }

  /**
   * The label lives in a bare text node between the cart icon and the "added" checkmark.
   * @param {HTMLButtonElement} button
   * @param {string} label
   */
  #setButtonLabel(button, label) {
    for (const node of button.childNodes) {
      if (node.nodeType !== Node.TEXT_NODE) continue;
      if (!node.textContent?.trim()) continue;
      if (node.textContent.trim() === label) return;

      node.textContent = label;
      return;
    }
  }

  get #addToCartComponents() {
    return this.#productForm?.querySelectorAll('add-to-cart-component') ?? [];
  }

  get #productForm() {
    return this.closest('product-form-component');
  }

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    if (!error) return;

    error.textContent = message;
    error.hidden = false;
  }

  #hideError() {
    const { error } = this.refs;
    if (!error) return;

    error.textContent = '';
    error.hidden = true;
  }

  /* -------------------------------------------------------------------------- */
  /* Add to cart                                                                 */
  /* -------------------------------------------------------------------------- */

  /**
   * Replaces `ProductFormComponent.handleSubmit` while bundles are active.
   * @param {Event} [event]
   */
  handleSubmit(event) {
    event?.preventDefault();

    if (this.#busy) return;

    const pack = this.#selectedPack;
    if (!pack) return;

    if (!this.#isComplete(pack)) {
      this.#showError(this.#quantityOnly ? this.dataset.soldOutText || 'This product is sold out.' : this.dataset.errorIncomplete || 'Please choose a flavor for every item.');
      pack.selects.find((select) => !this.#variants.get(select.value)?.available)?.focus();
      return;
    }

    this.#hideError();
    this.#busy = true;
    this.#addBundleToCart(pack).finally(() => {
      this.#busy = false;
    });
  }

  /**
   * Groups identical flavors into one line so a "Twin Pack" of the same flavor is a single
   * quantity-2 line rather than two lines.
   * @param {BundlePack} pack
   */
  #buildItems(pack) {
    const quantityVariant = this.#quantityVariant;
    const ids = this.#quantityOnly && quantityVariant
      ? Array(pack.size).fill(String(quantityVariant.id))
      : pack.selects.map((select) => select.value);
    const flavors = ids.map((id) => this.#variants.get(id)?.title ?? '').filter(Boolean);

    /** @type {Record<string, string>} */
    const properties = {};
    if (pack.discountCode) properties._bundle_discount_code = pack.discountCode;

    if (pack.size > 1) {
      properties[this.dataset.packPropertyLabel || 'Pack'] = pack.title;

      // A leading underscore keeps the property out of the cart and checkout display while still
      // recording it on the order.
      const flavorLabel =
        this.dataset.showFlavorProperty === 'true' ? this.dataset.flavorPropertyLabel || 'Flavors' : '_flavors';
      if (!this.#quantityOnly) properties[flavorLabel] = flavors.join(' + ');
    }

    /** @type {Map<string, {id: number, quantity: number, properties?: Record<string, string>}>} */
    const items = new Map();

    for (const id of ids) {
      const existing = items.get(id);
      if (existing) {
        existing.quantity += 1;
        continue;
      }

      items.set(id, {
        id: Number(id),
        quantity: 1,
        ...(Object.keys(properties).length ? { properties } : {}),
      });
    }

    return Array.from(items.values());
  }

  /** @param {BundlePack} pack */
  async #addBundleToCart(pack) {
    const items = this.#buildItems(pack);
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const sections = this.#cartSectionIds();
      const sectionData = sections ? { sections } : {};
      // Shopify accepts repeated quantities through its single-item format. This also covers
      // mixed triplets such as two Strawberry + one Banana; distinct variants can still batch.
      const payloads = items.length === 1 || items.some((item) => item.quantity > 1)
        ? items.map((item) => ({ ...item, ...sectionData }))
        : [{ items, ...sectionData }];
      /** @type {any} */
      let response;
      const result = await limitedCartRequest({
        productId: this.dataset.productId, limit: this.dataset.purchaseLimit,
        quantity: totalQuantity, cartUrl: Theme.routes.cart_url,
      }, async () => {
        let result;
        for (const payload of payloads) {
          result = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await result.clone().json();
          if (data.status || !result.ok) break;
        }
        return result;
      });
      response = await result.json();

      if (response.status) {
        this.#showError(response.description || response.message || 'Add to cart failed');
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );

        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'bundle-packs-component',
            sourceId: this.id,
            itemCount: totalQuantity,
            productId: this.dataset.productId,
          },
        });

        return;
      }

      const discountResponse = await this.#applyBundleDiscounts(sections);
      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'bundle-packs-component',
          sourceId: this.id,
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: discountResponse.sections ?? response.sections,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /**
   * Discount links persist across product pages. Update the cart explicitly instead, after the
   * items exist, and retain codes belonging to other bundles and manually entered discounts.
   * @param {string} sections
   */
  async #applyBundleDiscounts(sections) {
    const cart = await this.#fetchCart();
    const normalize = (/** @type {string} */ code) => code.trim().toLowerCase();
    const previous = String(cart.attributes?._bundle_discount_codes || '').split(',').filter(Boolean);
    const managed = new Set([...previous, ...this.#packs.map((pack) => pack.discountCode)].map(normalize));
    const bundleCodes = cart.items
      .map((/** @type {any} */ item) => item.properties?._bundle_discount_code)
      .filter((/** @type {any} */ code) => typeof code === 'string' && code.trim());
    const existing = (cart.discount_codes || [])
      .map((/** @type {any} */ discount) => discount.code)
      .filter((/** @type {string} */ code) => !managed.has(normalize(code)));
    const codes = [...new Map([...existing, ...bundleCodes].map((code) => [normalize(code), code])).values()];
    const result = await fetch(Theme.routes.cart_update_url || `${Theme.routes.cart_url}/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        attributes: { _bundle_discount_codes: [...new Set(bundleCodes)].join(',') },
        ...(sections ? { sections } : {}),
      }),
    });
    const data = await result.json();
    if (!result.ok || data.status) throw new Error(data.description || 'Could not update bundle discounts.');
    return data;
  }

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  async #fetchCart() {
    /** @type {any} */
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }
}

if (!customElements.get('bundle-packs-component')) {
  customElements.define('bundle-packs-component', BundlePacksComponent);
}
